import { useRef, useState } from 'react';
import type React from 'react';
import Papa from 'papaparse';
import { Upload, CheckCircle2, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useImportContacts } from '@/hooks/useContacts';

interface ParsedCSV {
  headers: string[];
  rows: string[][];
  file: File;
}

interface ImportResult {
  created: number;
  skipped: number;
  errors: Array<{ phone: string; reason: string }>;
}

type Step = 'select' | 'mapping' | 'done';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactImportDialog({ open, onOpenChange }: Props) {
  const [step, setStep] = useState<Step>('select');
  const [parsed, setParsed] = useState<ParsedCSV | null>(null);
  const [phoneCol, setPhoneCol] = useState('');
  const [nameCol, setNameCol] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: importCsv, isPending } = useImportContacts();

  function reset() {
    setStep('select');
    setParsed(null);
    setPhoneCol('');
    setNameCol('');
    setResult(null);
  }

  function handleClose() {
    onOpenChange(false);
    setTimeout(reset, 300);
  }

  function processFile(file: File) {
    if (!file.name.endsWith('.csv')) return;
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        const [headerRow, ...dataRows] = results.data;
        if (!headerRow) return;
        const headers = headerRow;
        setParsed({ headers, rows: dataRows.slice(0, 10), file });

        const phone = headers.find((h) =>
          ['phone', 'telefone', 'fone', 'cel', 'celular', 'whatsapp'].includes(h.toLowerCase()),
        );
        const name = headers.find((h) =>
          ['name', 'nome', 'contact', 'contato'].includes(h.toLowerCase()),
        );
        setPhoneCol(phone ?? '');
        setNameCol(name ?? '');
        setStep('mapping');
      },
    });
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) processFile(f);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) processFile(f);
  }

  function handleDownloadTemplate() {
    const csvText = Papa.unparse([
      ['phone', 'name'],
      ['54981250935', 'João da Silva'],
      ['+5511999990000', 'Maria Souza'],
    ]);
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'modelo-contatos.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  function handleImport() {
    if (!parsed || !phoneCol) return;

    const phoneIdx = parsed.headers.indexOf(phoneCol);
    const nameIdx = nameCol ? parsed.headers.indexOf(nameCol) : -1;

    // Re-read the full original file with papaparse and remap headers
    Papa.parse<string[]>(parsed.file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        const [, ...allDataRows] = results.data;
        const newHeaders = parsed.headers.map((h, i) => {
          if (i === phoneIdx) return 'phone';
          if (i === nameIdx) return 'name';
          return h;
        });
        const csvText = Papa.unparse([newHeaders, ...allDataRows]);
        const file = new File([csvText], 'import.csv', { type: 'text/csv' });

        importCsv(file, {
          onSuccess: (res) => {
            setResult(res.data.data);
            setStep('done');
          },
        });
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importar contatos via CSV</DialogTitle>
        </DialogHeader>

        {/* Step 1: file select */}
        {step === 'select' && (
          <div className="space-y-3">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-10 transition-colors ${
                dragOver ? 'border-primary bg-primary/5' : 'border-input'
              }`}
            >
              <Upload className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Arraste um arquivo CSV ou clique para selecionar
              </p>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                Selecionar arquivo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileInput}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                Primeira vez? Baixe o modelo com as colunas esperadas.
              </p>
              <Button variant="ghost" size="sm" onClick={handleDownloadTemplate}>
                <Download className="h-3.5 w-3.5" />
                Baixar modelo
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: column mapping + preview */}
        {step === 'mapping' && parsed && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Coluna de telefone *</Label>
                <Select value={phoneCol} onValueChange={setPhoneCol}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar coluna" />
                  </SelectTrigger>
                  <SelectContent>
                    {parsed.headers.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Coluna de nome (opcional)</Label>
                <Select value={nameCol} onValueChange={setNameCol}>
                  <SelectTrigger>
                    <SelectValue placeholder="Nenhuma" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Nenhuma</SelectItem>
                    {parsed.headers.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="overflow-auto rounded border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted">
                    {parsed.headers.map((h) => (
                      <th
                        key={h}
                        className={`px-3 py-2 text-left font-medium ${
                          h === phoneCol
                            ? 'text-primary'
                            : h === nameCol
                              ? 'text-green-600'
                              : 'text-muted-foreground'
                        }`}
                      >
                        {h}
                        {h === phoneCol && ' (telefone)'}
                        {h === nameCol && ' (nome)'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.map((row, i) => (
                    <tr key={i} className="border-b last:border-0">
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-1.5 text-muted-foreground">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              Exibindo até 10 linhas. Telefones aceitam DDD + número, com ou sem +55 (ex:
              54981250935 ou +5554981250935).
            </p>
          </div>
        )}

        {/* Step 3: done */}
        {step === 'done' && result && (
          <div className="space-y-4 py-4 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
            <div>
              <p className="text-lg font-semibold">Importação concluída</p>
              <p className="mt-1 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{result.created}</span> novos ·{' '}
                <span className="font-medium text-foreground">{result.skipped}</span> já existentes
              </p>
            </div>
            {result.errors.length > 0 && (
              <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-left text-xs">
                <p className="mb-1 font-medium text-destructive">{result.errors.length} erro(s):</p>
                {result.errors.slice(0, 5).map((e, i) => (
                  <p key={i} className="text-muted-foreground">
                    {e.phone}: {e.reason}
                  </p>
                ))}
                {result.errors.length > 5 && (
                  <p className="text-muted-foreground">…e mais {result.errors.length - 5}</p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'select' && (
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
          )}
          {step === 'mapping' && (
            <>
              <Button variant="outline" onClick={reset}>
                Voltar
              </Button>
              <Button onClick={handleImport} disabled={!phoneCol || isPending}>
                {isPending ? 'Importando...' : 'Importar'}
              </Button>
            </>
          )}
          {step === 'done' && (
            <>
              <Button variant="outline" onClick={reset}>
                Importar outro
              </Button>
              <Button onClick={handleClose}>Concluir</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
